import React, { ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { lineHeight, theme, toRem } from 'design';
import { Tooltip } from '../Tooltip';

interface IButtonTextProps {
  className?: string;
  disabled?: boolean;
  fontWeight?: any;
  fontSize?: number;
  isLoading?: boolean;
  label?: string;
  onClick?: () => void;
  to?: string;
  tooltip?: string | ReactNode;
}

const buttonStyles = css<Partial<IButtonTextProps>>`
  font-family: ${theme.font.family.euclid};
  font-weight: ${({ fontWeight }) => fontWeight || theme.font.weight.regular};
  font-size: ${({ fontSize }) => (fontSize ? toRem(fontSize) : toRem(theme.font.size.md))};
  line-height: ${({ fontSize }) => lineHeight(fontSize || theme.font.size.md)};
  text-decoration: underline;
  color: ${theme.color.neutral[700]};
  cursor: pointer;
  transition: color ${theme.transition.button.fast};

  &:hover {
    color: ${theme.color.neutral[800]};
  }

  &:active,
  &:visited {
    color: ${theme.color.neutral[700]};
  }
`;

const Button = styled.button.attrs({
  type: 'button',
})`
  ${buttonStyles}
`;

const StyledLink = styled(Link)`
  ${buttonStyles}
`;

const TooltipWrapper = styled.span`
  flex-grow: 0;
  flex-shrink: 1;
  width: auto;
`;

const StyledTooltip = styled(Tooltip)`
  text-align: left;
`;

const ButtonText = ({ className, disabled, fontSize, fontWeight, isLoading, label = 'Skip', onClick, to, tooltip, ...rest }: IButtonTextProps) => {
  const content = isLoading ? (
    <Skeleton width={50} />
  ) : tooltip ? (
    <TooltipWrapper data-tooltip-id={label}>
      {label}
      <StyledTooltip id={label} place="right">
        {tooltip}
      </StyledTooltip>
    </TooltipWrapper>
  ) : (
    label
  );
  const disabledProp = disabled && { disabled };

  if (to) {
    return (
      <StyledLink fontWeight={fontWeight} fontSize={fontSize} to={to} onClick={onClick} className={className} {...disabledProp} {...rest}>
        {content}
      </StyledLink>
    );
  }
  return (
    <Button fontWeight={fontWeight} fontSize={fontSize} className={className} onClick={onClick} {...disabledProp} {...rest}>
      {content}
    </Button>
  );
};

export default ButtonText;
